// /src/ui/components/PartBrowser.tsx

import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PARTS, PART_CATEGORIES } from '@/engine/partsCatalog';
import { MAIN_SUBMODEL_ID, useModelStore } from '@/store/modelStore';
import { COLORS, loadLDrawColors } from '@/engine/ldrawColors';
import { LDRAW_BASE, LDRAW_INDEX_BASE } from '@/engine/ldrawConfig';
import PartIcon from '@/ui/components/PartIcon';

type PartsIndexEntry = {
  file: string;      // e.g. "3001.dat" or "s/3001s01.dat"
  name: string;      // parsed "0 Name:" (or file fallback)
  category: string;  // parsed "!CATEGORY" (or empty)
  keywords: string[];// parsed "!KEYWORDS" (or empty)
  // Precomputed search fields (lowercase)
  _fileLower: string;
  _nameLower: string;
  _catLower: string;
  _search: string;
  _searchCompact: string;
};

type MenuManifest = {
  version: number;
  pageSize: number;
  categories: Array<{ key: string; slug?: string; count: number; pages: number }>;
};

const PARTS_INDEX_URL = `${LDRAW_INDEX_BASE}parts-index.json`;
const PARTS_INDEX_LIMIT = 120;

const MENU_MANIFEST_URL = `${LDRAW_INDEX_BASE}parts-menu/manifest.json`;

function menuPageUrl(slug: string, page: number) {
  // slug is percent-encoded at build time; do NOT encode again here.
  return `${LDRAW_INDEX_BASE}parts-menu/${slug}/page-${page}.json`;
}

function compactText(s: string): string {
  return (s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function inferPartCategoryFromLdraw(raw: string): string {
  const c = (raw ?? '').toLowerCase();
  if (!c) return 'Basics';
  if (
    c.includes('technic') ||
    c.includes('axle') ||
    c.includes('gear') ||
    c.includes('pin')
  ) return 'Technic';
  if (c.includes('tile')) return 'Tiles';
  if (c.includes('plate')) return 'Plates';
  if (c.includes('brick')) return 'Bricks';
  return 'Basics';
}

function toIndexEntry(raw: unknown): PartsIndexEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as any;
  const file = String(r.file ?? '').trim();
  if (!file) return null;
  const name = String(r.name ?? file).trim() || file;
  const category = String(r.category ?? '').trim();
  const keywords = Array.isArray(r.keywords)
    ? r.keywords.map((k: any) => String(k ?? '').trim()).filter(Boolean)
    : [];

  const fileLower = file.toLowerCase();
  const nameLower = name.toLowerCase();
  const catLower = category.toLowerCase();
  const search = `${fileLower} ${nameLower} ${catLower} ${keywords.join(' ').toLowerCase()}`.trim();
  const searchCompact = compactText(search);

  return {
    file,
    name,
    category,
    keywords,
    _fileLower: fileLower,
    _nameLower: nameLower,
    _catLower: catLower,
    _search: search,
    _searchCompact: searchCompact,
  };
}

function searchPartsIndex(
  items: PartsIndexEntry[],
  queryLower: string,
  categoryFilter: string,
  limit: number
): PartsIndexEntry[] {
  const q = (queryLower ?? '').trim().toLowerCase();
  if (!q) return [];

  const tokens = q.split(/\s+/).filter(Boolean);
  const tokensCompact = tokens.map((t) => compactText(t));
  const qCompact = compactText(q);
  const wantsSubpaths = q.includes('/'); // if user searches "s/..." allow subparts

  const candidates: Array<{ item: PartsIndexEntry; score: number }> = [];
  const sortFn = (a: { item: PartsIndexEntry; score: number }, b: { item: PartsIndexEntry; score: number }) =>
    a.score - b.score || a.item.file.localeCompare(b.item.file);

  const filterRaw = String(categoryFilter ?? '').trim();
  const filterLower = filterRaw.toLowerCase();
  const isNoFilter = !filterRaw || filterLower === 'all' || filterLower === 'basics';
  const isBuiltin = (PART_CATEGORIES as unknown as string[]).map((x) => String(x)).some((x) => x.toLowerCase() === filterLower);

  const pushCandidate = (item: PartsIndexEntry, score: number) => {
    const entry = { item, score };
    if (candidates.length < limit) {
      candidates.push(entry);
      candidates.sort(sortFn);
      return;
    }
    const worst = candidates[candidates.length - 1];
    if (score >= worst.score) return;
    candidates[candidates.length - 1] = entry;
    candidates.sort(sortFn);
  };

  const qHasExt = /\.[a-z0-9]{2,4}$/i.test(q);

  for (const it of items) {
    // Hide subparts by default (keeps results usable); opt-in via "/" search.
    if (!wantsSubpaths && it.file.includes('/')) continue;

    // Category filter:
    // - "All" / "Basics" (legacy) => no filtering
    // - If the filter is one of our built-in buckets (Bricks/Plates/Tiles/Technic/Basics),
    //   use inference for back-compat.
    // - Otherwise match the raw LDraw !CATEGORY exactly (case-insensitive).
    if (!isNoFilter) {
      if (isBuiltin) {
        const inferred = inferPartCategoryFromLdraw(it._catLower);
        if (String(inferred).toLowerCase() !== filterLower) continue;
      } else {
        if ((it._catLower ?? '').trim() !== filterLower) continue;
      }
    }

    // Token match (AND). Match against normal + compact strings.
    let ok = true;
    for (let i = 0; i < tokens.length; i++) {
      const t = tokens[i];
      const tc = tokensCompact[i];
      if (!it._search.includes(t) && (tc ? !it._searchCompact.includes(tc) : true)) {
        ok = false;
        break;
      }
    }
    if (!ok) continue;

    // Simple relevance scoring (lower is better).
    let score = 0;
    const file = it._fileLower;
    const name = it._nameLower;

    if (file === q || (!qHasExt && file === `${q}.dat`)) score -= 120000;
    if (file.startsWith(q)) score -= 80000;
    else if (file.includes(q)) score -= 45000;

    if (name === q) score -= 30000;
    else if (name.startsWith(q)) score -= 20000;
    else if (name.includes(q)) score -= 12000;

    if (qCompact && it._searchCompact.includes(qCompact)) score -= 6000;

    // Prefer shorter ids/names when otherwise similar.
    score += Math.min(2000, file.length * 2) + Math.min(2000, name.length);

    pushCandidate(it, score);
  }

  candidates.sort(sortFn);
  return candidates.map((c) => c.item);
}

export default function PartBrowser() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>('All');
  const [color, setColor] = useState<number>(4);
  const [colors, setColors] = useState(COLORS);
  const [partsIndex, setPartsIndex] = useState<PartsIndexEntry[] | null>(null);
  const [partsIndexStatus, setPartsIndexStatus] = useState<'idle' | 'loading' | 'ready' | 'missing' | 'error'>('idle');
  const indexAbortRef = useRef<AbortController | null>(null);

  const [menuManifest, setMenuManifest] = useState<MenuManifest | null>(null);
  const [menuStatus, setMenuStatus] = useState<'idle' | 'loading' | 'ready' | 'missing' | 'error'>('idle');
  const [menuCache, setMenuCache] = useState<Record<string, { items: PartsIndexEntry[]; loadedPages: number; loading: boolean; error?: boolean }>>({});
  const menuAbortRef = useRef<AbortController | null>(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0, visible: false });

  useEffect(() => {
    loadLDrawColors(LDRAW_BASE)
      .then(setColors)
      .catch(() => {});
  }, []);

  // Load the (small) paged menu manifest once.
  useEffect(() => {
    const ctl = new AbortController();
    setMenuStatus('loading');
  
    (async () => {
      try {
        const res = await fetch(MENU_MANIFEST_URL, { cache: 'force-cache' as any, signal: ctl.signal });
        if (!res.ok) {
          setMenuManifest(null);
          setMenuStatus(res.status === 404 ? 'missing' : 'error');
          return;
        }
        const json = await res.json();
        if (!json || typeof json !== 'object' || !Array.isArray((json as any).categories)) {
          setMenuManifest(null);
          setMenuStatus('error');
          return;
        }
  
        const raw = json as any;
        const cats = (raw.categories as any[])
          .map((c) => {
            const key = String(c?.key ?? '').trim();
            if (!key) return null;
            const slug = String(c?.slug ?? encodeURIComponent(key));
            const count = Math.max(0, Math.floor(Number(c?.count ?? 0)));
            const pages = Math.max(1, Math.floor(Number(c?.pages ?? 1)));
            return { key, slug, count, pages };
          })
          .filter(Boolean) as Array<{ key: string; slug: string; count: number; pages: number }>;
  
        setMenuManifest({
          version: Math.max(1, Math.floor(Number(raw.version ?? 1))),
          pageSize: Math.max(1, Math.floor(Number(raw.pageSize ?? 400))),
          categories: cats,
        });
        setMenuStatus('ready');
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setMenuManifest(null);
        setMenuStatus('error');
      }
    })();
  
    return () => ctl.abort();
  }, []);

  // Once we know the real categories, ensure the current selection exists.
  useEffect(() => {
    if (menuStatus !== 'ready') return;
    const cats = menuManifest?.categories ?? [];
    if (!cats.length) return;
    const keys = new Set(cats.map((c) => c.key));
    if (!keys.has(category)) setCategory(cats[0].key);
  }, [menuStatus, menuManifest, category]);

  const menuMeta = useMemo(() => {
    return menuManifest?.categories?.find((c) => c.key === category) ?? null;
  }, [menuManifest, category]);

  // Current category cache entry (used by browse rendering + "load more").
  // IMPORTANT: this must be included in memo deps where it affects output.
  const menuCur = menuCache[String(category)];
  const menuCurItems = menuCur?.items ?? null;

  const slugForCategory = (catKey: string) => {
    const entry = menuManifest?.categories?.find((c) => c.key === catKey);
    // slug is already percent-encoded at build time
    return entry?.slug ?? encodeURIComponent(String(catKey));
  };

  const loadMenuPage = async (cat: string, page: number) => {
    if (menuStatus !== 'ready' || !menuManifest) return;
    if (page < 0) return;

    // Prevent duplicate loads
    const existing = menuCache[String(cat)];
    if (existing?.loading) return;
    if (existing && existing.loadedPages > page) return;

    const ctl = new AbortController();
    menuAbortRef.current?.abort();
    menuAbortRef.current = ctl;

    setMenuCache((prev) => ({
      ...prev,
      [String(cat)]: {
        items: prev[String(cat)]?.items ?? [],
        loadedPages: prev[String(cat)]?.loadedPages ?? 0,
        loading: true,
        error: false,
      },
    }));

    try {
      const slug = slugForCategory(cat);
      const res = await fetch(menuPageUrl(slug, page), { cache: 'force-cache' as any, signal: ctl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!Array.isArray(json)) throw new Error('Invalid menu page');

      const nextItems: PartsIndexEntry[] = [];
      for (const it of json) {
        const e = toIndexEntry(it);
        if (e) nextItems.push(e);
      }

      setMenuCache((prev) => {
        const cur = prev[String(cat)] ?? { items: [], loadedPages: 0, loading: false };
        const merged = page === 0 ? nextItems : [...(cur.items ?? []), ...nextItems];
        return {
          ...prev,
          [String(cat)]: { items: merged, loadedPages: Math.max(cur.loadedPages ?? 0, page + 1), loading: false, error: false },
        };
      });
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      setMenuCache((prev) => ({
        ...prev,
        [String(cat)]: {
          items: prev[String(cat)]?.items ?? [],
          loadedPages: prev[String(cat)]?.loadedPages ?? 0,
          loading: false,
          error: true,
        },
      }));
    } finally {
      menuAbortRef.current = null;
    }
  };

  // Browse mode: ensure page 0 for selected category is loaded (when no query)
  useEffect(() => {
    const hasQuery = !!query.trim();
    if (hasQuery) return;
    if (menuStatus !== 'ready') return;

    const cur = menuCache[String(category)];
    if (cur && cur.loadedPages >= 1) return;
    void loadMenuPage(category, 0);
  }, [query, category, menuStatus, menuCache]);

  // Lazy-load the full LDraw parts index only when the user starts searching.
  useEffect(() => {
    return () => {
      indexAbortRef.current?.abort();
      indexAbortRef.current = null;
      menuAbortRef.current?.abort();
      menuAbortRef.current = null;
    };
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (!q) return;
    if (partsIndexStatus !== 'idle') return;

    const ctl = new AbortController();
    indexAbortRef.current = ctl;
    setPartsIndexStatus('loading');

    (async () => {
      try {
        const res = await fetch(PARTS_INDEX_URL, { cache: 'force-cache' as any, signal: ctl.signal });
        if (!res.ok) {
          setPartsIndex([]);
          setPartsIndexStatus(res.status === 404 ? 'missing' : 'error');
          return;
        }
        const json = await res.json();
        if (!Array.isArray(json)) {
          setPartsIndex([]);
          setPartsIndexStatus('error');
          return;
        }
        const items: PartsIndexEntry[] = [];
        for (const it of json) {
          const e = toIndexEntry(it);
          if (e) items.push(e);
        }
        setPartsIndex(items);
        setPartsIndexStatus('ready');
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setPartsIndex([]);
        setPartsIndexStatus('error');
      } finally {
        indexAbortRef.current = null;
      }
    })();
  }, [query, partsIndexStatus]);

  const mode = useModelStore((s) => s.mode);
  const uiMode = useModelStore((s) => s.uiMode);
  const pendingPlacement = useModelStore((s) => s.pendingPlacement);
  const currentStep = useModelStore((s) => s.currentStep);
  const submodels = useModelStore((s) => s.submodels);
  const activeSubmodelId = useModelStore((s) => s.activeSubmodelId);
  const setPendingPlacement = useModelStore((s) => s.setPendingPlacement);
  const pushToast = useModelStore((s) => s.pushToast);

  useEffect(() => {
    if (!pendingPlacement) {
      setCursorPos((p) => ({ ...p, visible: false }));
      return;
    }
    const onMove = (ev: PointerEvent) => {
      setCursorPos({ x: ev.clientX, y: ev.clientY, visible: true });
    };
    const onBlur = () => {
      setCursorPos((p) => ({ ...p, visible: false }));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('blur', onBlur);
    };
  }, [pendingPlacement]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const hasQuery = !!q;

    // No search text -> browse full library by category (paged) if available.
    if (!hasQuery) {
      const cached = menuCur;
      if (menuStatus === 'ready' && cached && Array.isArray(cached.items) && cached.items.length) {
        return cached.items;
      }
      // Fallback: curated built-in catalog.
      return PARTS.filter((p) => (category === 'All' || category === 'Basics' ? true : p.category === category));
     }

    // Search text -> prefer full-library search (parts-index.json) when available.
    if (partsIndexStatus === 'ready' && Array.isArray(partsIndex) && partsIndex.length) {
      return searchPartsIndex(partsIndex, q, category, PARTS_INDEX_LIMIT);
    }

    // While loading, avoid showing misleading "no results" from the tiny built-in list.
    if (partsIndexStatus === 'idle' || partsIndexStatus === 'loading') {
      return [];
    }

    // Missing/error -> fall back to built-in catalog search.
    return PARTS
      .filter((p) => (category === 'All' || category === 'Basics' ? true : p.category === category))
      .filter((p) => (q ? p.name.toLowerCase().includes(q) || p.file.toLowerCase().includes(q) : true));
  }, [query, category, partsIndex, partsIndexStatus, menuStatus, menuCurItems]);

  const colorHex = useMemo(() => colors.find((c) => c.id === color)?.hex ?? '#c0c0c0', [colors, color]);
  const activeSubmodelName = useMemo(() => {
    if (!activeSubmodelId) return 'Main model';
    return submodels.find((sm) => sm.id === activeSubmodelId)?.name ?? activeSubmodelId;
  }, [activeSubmodelId, submodels]);
  
  const hasQuery = !!query.trim();
  const usingIndexSearch = hasQuery && partsIndexStatus === 'ready' && Array.isArray(partsIndex) && partsIndex.length > 0;
  const usingMenuBrowse = !hasQuery && menuStatus === 'ready' && !!menuMeta;

  const canLoadMore =
    usingMenuBrowse &&
    !!menuMeta &&
    !!menuCur &&
    !menuCur.loading &&
    (menuCur.loadedPages ?? 0) < (menuMeta.pages ?? 0);

    const categoriesForSelect = useMemo(() => {
    const fromMenu = menuStatus === 'ready' && menuManifest?.categories?.length
      ? menuManifest.categories.map((c) => c.key)
      : null;
    if (fromMenu && fromMenu.length) return fromMenu;
    // Fallback categories if menu manifest is missing:
    return ['All', ...(PART_CATEGORIES as unknown as string[])];
  }, [menuStatus, menuManifest]);
  
   return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="panelHeader">
        <div>
          <div className="title">Parts</div>
          <div className="hint">Click a part, then click in the scene to place.</div>
        </div>
      </div>

      <div className="panelBody">
        <div className="card">
          <div className="field">
            <label>Search</label>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="3001.dat, 2x4…" />
          </div>
          <div className="field">
            <label>Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value as any)}>
              {categoriesForSelect.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Color</label>
            <select value={color} onChange={(e) => setColor(Number(e.target.value))}>
              {colors.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.id})</option>
              ))}
            </select>
          </div>

          <div className="hr" />
          <div className="muted">
            <b>Library note:</b> this prototype works with fallback geometry out of the box.
            For real LEGO part geometry, drop the LDraw library into <span className="mono">public/ldraw/</span>
            (see the repo&apos;s README).
          </div>
          <div className="help" style={{ marginTop: 10 }}>
            <b>Adding to:</b> <span className="mono">{activeSubmodelName}</span> · step <span className="mono">{currentStep}</span>
            {uiMode === 'instructions' && (
              <>
                <div className="hr" />
                <b>Instruction mode:</b> editing is disabled. Switch back to Edit to add parts.
              </>
            )}
          </div>
          {pendingPlacement && uiMode === 'edit' && (
            <div className="help" style={{ marginTop: 10 }}>
              <b>Placement mode:</b> <span className="mono">{pendingPlacement.name}</span> · click in the scene to place ·
              <span className="mono"> Esc</span> to cancel
            </div>
          )}

          {hasQuery && (
            <div className="help" style={{ marginTop: 10 }}>
              <b>Search mode:</b>{' '}
              {partsIndexStatus === 'ready' ? (
                <>
                  full library · cap <span className="mono">{PARTS_INDEX_LIMIT}</span>
                  {Array.isArray(partsIndex) && partsIndex.length > 0 && (
                    <>
                      {' '}· index <span className="mono">{partsIndex.length.toLocaleString()}</span>
                    </>
                  )}
                </>
              ) : partsIndexStatus === 'loading' || partsIndexStatus === 'idle' ? (
                <>loading full library index…</>
              ) : partsIndexStatus === 'missing' ? (
                <>
                  missing <span className="mono">parts-index.json</span> · run <span className="mono">npm run build-ldraw-filemap</span>
                </>
              ) : (
                <>
                  could not load <span className="mono">parts-index.json</span> · using built-in list
                </>
              )}
            </div>
          )}

           {!hasQuery && (
            <div className="help" style={{ marginTop: 10 }}>
              <b>Browse mode:</b>{' '}
              {menuStatus === 'ready' ? (
                <>
                  full library (paged)
                  {menuMeta && (
                    <>
                      {' '}· <span className="mono">{menuMeta.count.toLocaleString()}</span> parts ·{' '}
                      <span className="mono">{(menuCur?.loadedPages ?? 0)}/{menuMeta.pages}</span> pages loaded
                    </>
                  )}
                </>
              ) : menuStatus === 'loading' || menuStatus === 'idle' ? (
                <>loading category menu…</>
              ) : menuStatus === 'missing' ? (
                <>missing <span className="mono">parts-menu</span> · run <span className="mono">npm run build-ldraw-filemap</span></>
              ) : (
                <>could not load parts menu · using built-in list</>
              )}
            </div>
          )}

          {mode === 'advanced' && (
            <div className="help" style={{ marginTop: 10 }}>
              Advanced mode will also snap rotations on all axes (useful for Technic / hinges later).
            </div>
          )}
        </div>

        <div style={{ height: 12 }} />

        <div className="list">
          {usingIndexSearch && filtered.length === PARTS_INDEX_LIMIT && (
            <div className="muted">
              Showing top <span className="mono">{PARTS_INDEX_LIMIT}</span> results. Refine your search to narrow.
            </div>
          )}
          
          {filtered.map((p) => (
            <div
              className={clsx('partRow', pendingPlacement?.partFile === p.file && 'placing')}
              key={p.file}
              onClick={(ev) => {
                if (uiMode !== 'edit') {
                  pushToast({ title: 'Editing disabled', detail: 'Switch to Edit mode to add parts.', kind: 'warn' });
                  return;
                }
                setPendingPlacement({
                  name: p.name,
                  partFile: p.file,
                  ldrawColor: color,
                  colorHex,
                  step: currentStep,
                  submodelId: activeSubmodelId ?? MAIN_SUBMODEL_ID,
                });
                setCursorPos({ x: ev.clientX, y: ev.clientY, visible: true });
              }}
              title={`Pick ${p.name} for placement`}
            >
              <PartIcon
                className="swatch partIcon"
                name={p.name}
                partFile={p.file}
                colorHex={colorHex}
              />
              <div>
                <div style={{ fontSize: 13 }}>{p.name}</div>
                <div className="muted mono">{p.file}</div>
                {(usingIndexSearch || usingMenuBrowse) && (p as any).category && (
                  <div className="muted">{String((p as any).category)}</div>
                )}
              </div>
            </div>
          ))}

          {usingMenuBrowse && menuCur?.loading && (
            <div className="muted">Loading category page…</div>
          )}
          {usingMenuBrowse && menuCur?.error && (
            <div className="muted">Could not load more parts for this category.</div>
          )}
          {canLoadMore && (
            <button
              className="btn"
              onClick={() => void loadMenuPage(category, menuCur?.loadedPages ?? 0)}
              type="button"
              style={{ marginTop: 10 }}
            >
              Load more…
            </button>
          )}

          {filtered.length === 0 && hasQuery && (partsIndexStatus === 'idle' || partsIndexStatus === 'loading') && (
            <div className="muted">Loading full library index…</div>
          )}
          {filtered.length === 0 && !(hasQuery && (partsIndexStatus === 'idle' || partsIndexStatus === 'loading')) && (
            <div className="muted">No parts match that search.</div>
          )}
        </div>
      </div>

      {pendingPlacement && cursorPos.visible && (
        <div className="placementCursor" style={{ left: cursorPos.x, top: cursorPos.y }}>
          <PartIcon
            className="placementCursorSwatch partIcon"
            name={pendingPlacement.name}
            partFile={pendingPlacement.partFile}
            colorHex={pendingPlacement.colorHex}
          />
          <span className="placementCursorLabel mono">{pendingPlacement.name}</span>
        </div>
      )}
    </div>
  );
}
